import React, { useEffect, useState, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useHistory, Link } from "react-router-dom";
import { useSpecialism, specialismCodeMap, specialismCollectionMap } from "../contexts/SpecialismContext";
import { articleFields } from "../types/Article";
import type { Article } from "../types/Article"; // <-- Use shared types

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: "18px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  padding: "32px",
  maxWidth: "700px",
  width: "100%",
  minWidth: "320px",
  margin: "10px auto",
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  fontFamily: "Roboto, Arial, sans-serif",
  position: "relative",
};

const labelStyle: React.CSSProperties = {
  color: "#888",
  fontSize: "1.2rem",
  marginBottom: "4px",
};

const textStyle: React.CSSProperties = {
  color: "#444",
  fontSize: "0.95rem",
  whiteSpace: "normal",
  fontFamily: "inherit",
};

const editButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: "24px",
  right: "24px",
  background: "#1976d2",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(25,118,210,0.15)",
  zIndex: 2,
};

const responsiveStyle = `
  @media (max-width: 700px) {
    .article-view-card {
      max-width: 98vw !important;
      width: 98vw !important;
      padding: 16px !important;
    }
    .article-view-text {
      font-size: 0.98rem !important;
    }
    .article-view-label {
      font-size: 0.92rem !important;
    }
    .article-view-title {
      font-size: 1.3rem !important;
    }
  }
`;

interface ArticleViewProps {
  articleId?: string;
  specialism?: string; // This is the full name, e.g. "Internegeneeskunde"
}

const ArticleView: React.FC<ArticleViewProps> = ({ articleId, specialism }) => {
  const context = useSpecialism();
  const [specialismSetFromUrl, setSpecialismSetFromUrl] = useState(false);

  // Find the code for the given specialism name
  const codeFromName = Object.entries(specialismCodeMap)
    .find(([code, name]) => name === specialism)?.[0] as keyof typeof specialismCollectionMap;

  // Use code from props if possible, else fallback to context
  const specialismCode = codeFromName || context.specialismCode;

  // Only set specialism context from URL once
  useEffect(() => {
    if (codeFromName) {
      if (!specialismSetFromUrl) {
        if (codeFromName !== context.specialismCode) {
          context.setSpecialismCode(codeFromName);
        }
        setSpecialismSetFromUrl(true);
      }
    }
    // If no codeFromName, don't overwrite manual changes
  }, [codeFromName, context.specialismCode, specialismSetFromUrl]);

  const [article, setArticle] = useState<Article | null>(null);
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null);
  const history = useHistory();
  const collectionName = specialismCollectionMap[specialismCode] || "articles";

  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) return;
      const docSnap = await getDoc(doc(db, collectionName, articleId));
      if (docSnap.exists()) {
        setArticle({ id: docSnap.id, ...docSnap.data() } as Article);
      }
    };
    fetchArticle();
  }, [articleId, collectionName]);

  if (!article) {
    return (
      <div style={{ textAlign: "center", marginTop: "80px", color: "#888" }}>
        Loading article...
      </div>
    );
  }

  const handleEdit = () => {
    history.push({
      pathname: "/editing",
      state: { editArticle: article },
    });
  };

  // Helper to render a field card
  const renderFieldCard = (label: string, value?: string) => {
    if (!value) return null;
    return (
      <div className="article-view-card" style={cardStyle}>
        <div className="article-view-label" style={labelStyle}>{label}</div>
        <div
          className="article-view-text"
          style={textStyle}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      </div>
    );
  };

  const images = article.imageUrls || [];
  const firstImage = images.length > 0 ? images[0] : null;
  const otherImages = images.length > 1 ? images.slice(1) : [];

  const FullscreenImage: React.FC<{ src: string }> = ({ src }) => (
    <div
      onClick={() => setFullscreenImg(null)}
      style={{
        position: "fixed",
        top: 0, left: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.85)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer"
      }}
    >
      <img
        src={src}
        alt="Full screen"
        style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          borderRadius: "18px",
          boxShadow: "0 2px 24px rgba(0,0,0,0.25)",
          background: "#222",
          cursor: "default"
        }}
        onClick={e => e.stopPropagation()}
      />
    </div>
  );

  return (
    <>
      <style>{responsiveStyle}</style>
      <div style={{
        minHeight: "100vh",
        width: "100%",
        background: "#f5f6fa",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        position: "relative",
      }}>
        {firstImage && (
          <div style={{ marginTop: 32, marginBottom: 16, width: "100%", display: "flex", justifyContent: "center" }}>
            <img
              src={firstImage}
              alt="Article"
              style={{
                width: "100%",
                maxWidth: "700px",
                height: "auto",
                borderRadius: "18px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                objectFit: "contain",
                display: "block",
                cursor: "pointer"
              }}
              onClick={() => setFullscreenImg(firstImage)}
            />
          </div>
        )}
        <div className="article-view-card" style={{ ...cardStyle, position: "relative" }}>
          <button
            style={editButtonStyle}
            onClick={handleEdit}
            aria-label="Edit Article"
            title="Edit Article"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="22"
              width="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
          </button>
          <h1 className="article-view-title" style={{ fontSize: "2rem", fontWeight: "bold", color: "#222", margin: 0 }}>
            {article.title}
          </h1>
          {/* Category */}
          <div>
            <div className="article-view-label" style={labelStyle}>Category</div>
            <div style={{ fontWeight: "bold", color: "#1976d2" }}>
              {article.category
                ? article.category.charAt(0).toUpperCase() + article.category.slice(1)
                : <span style={{ color: "#aaa" }}>Geen categorie</span>}
            </div>
          </div>
          {/* Summary */}
          <div>
            <div className="article-view-label" style={labelStyle}>Summary</div>
            <div
              className="article-view-text"
              style={textStyle}
              dangerouslySetInnerHTML={{ __html: article.summary }}
            />
          </div>
        </div>
        {/* Dynamically render all other fields */}
        {articleFields
          .filter(field =>
            field.type === "richtext" &&
            field.name !== "summary" &&
            field.name !== "category" &&
            field.name !== "title"
          )
          .map(field =>
            renderFieldCard(field.label, article[field.name as keyof typeof article] as string)
          )}
        {otherImages.length > 0 && (
          <div style={{
            marginTop: 32,
            marginBottom: 32,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "18px",
            width: "100%",
          }}>
            {otherImages.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt={`Article ${idx + 2}`}
                style={{
                  width: "100%",
                  maxWidth: "700px",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  objectFit: "contain",
                  display: "block",
                  cursor: "pointer"
                }}
                onClick={() => setFullscreenImg(url)}
              />
            ))}
          </div>
        )}
        {fullscreenImg && <FullscreenImage src={fullscreenImg} />}
      </div>
      <style>{`
        .article-view-card {
          width: 100% !important;
          max-width: 700px !important;
          box-sizing: border-box !important;
          padding: 16px !important;
          margin: 10px auto !important;
        }
        .article-view-text,
        .article-view-text * {
          font-size: ${textStyle.fontSize} !important;
          font-family: inherit !important;
        }
        .article-view-text img {
          max-width: 95% !important;
          height: auto !important;
          display: block;
          margin: 12px auto;
        }
        .article-view-text ul,
        .article-view-text ol {
          margin-top: 0 !important;
        }
      `}</style>
    </>
  );
};

export default ArticleView;